import { freeze } from 'icepick';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import zipObject from 'lodash/zipObject';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { BASE_HIERARCHY_TYPE } from '@atlassian/jira-issue-type-hierarchies';
import { TimeEstimateKey } from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import type {
	aggBacklogDataQuery$data,
	JiraTimeFormat,
	JiraTimeUnit,
} from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type { FeaturesResponse } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import { CAN_ARCHIVE_ISSUE } from '@atlassian/jira-software-board/src/model/permission/permission-types';
import { TimeFormats, TimeUnits } from '@atlassian/jira-time-tracking-formatter/src/constants.tsx';
import type { TimeFormat, TimeUnit } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { getIdFromAri } from '../../../common/utils';
import {
	CAN_CREATE_ISSUES,
	CAN_EDIT_BOARD_CONFIG,
	CAN_EDIT_ISSUES,
	CAN_MANAGE_SPRINTS,
} from '../../../model/config/permissions/constants';
import {
	BACKLOG,
	BOARD,
	CREATE_ISSUE,
	EDIT_BOARD_CONFIG,
	EDIT_ISSUE,
	ESTIMATION_TYPE_ID_PREFIX,
	ISSUE_COUNT,
	ISSUE_COUNT_ID,
	ISSUE_LIST,
	MANAGE_SPRINT,
	NONE,
	ORIGINAL_ESTIMATE,
	ORIGINAL_ESTIMATE_FIELD_ID,
	SPRINT,
	SPRINT_STATE_FUTURE,
	STORY_POINTS,
	ARCHIVE_ISSUE,
} from '../../../model/constants';
import {
	ENABLED,
	ESTIMATION_FEATURE,
	SPRINTS_FEATURE,
	VERSION_FEATURE,
} from '../../../model/graphql';
import type {
	AggBoardScope,
	AggEstimation,
	AggProjectLocation,
	AggSoftwareCard,
	AggSoftwareCardParent,
	AggSoftwareCardType,
	AggSprint,
	BacklogStrategy,
	Card,
	CardList,
	CardType,
	EstimationType,
	SprintState,
} from '../../../model/types';
import type { ConfigPayload, SetAppDataPayload } from '../../../state/actions/app';
import type { CardEntityState } from '../../../state/reducers/entities/card/types';
import type { EstimationEntityState } from '../../../state/reducers/entities/estimation/types';
import type { IssueParentsEntityState } from '../../../state/reducers/entities/issue-parent/types';
import type { ProjectState } from '../../../state/reducers/entities/project/types';
import type { VersionState } from '../../../state/reducers/entities/version/types';

const storyPointFieldIdTransformer = (estimation: AggBoardScope['estimation']): string | null => {
	const storyPointId =
		estimation.available.find(
			({ statisticFieldId }) =>
				statisticFieldId !== ORIGINAL_ESTIMATE_FIELD_ID && statisticFieldId !== ISSUE_COUNT_ID,
		)?.statisticFieldId || '';

	return storyPointId.replace(ESTIMATION_TYPE_ID_PREFIX, '') || null;
};

const isFeatureEnabled = (features: FeaturesResponse[], featureKey: string): boolean =>
	(features || []).some(({ key, status }) => String(key) === featureKey && status === ENABLED);

const getBacklogStrategy = (features: FeaturesResponse[]): BacklogStrategy => {
	if (!isFeatureEnabled(features, BACKLOG)) {
		return NONE;
	}
	if (isFeatureEnabled(features, SPRINTS_FEATURE)) {
		return SPRINT;
	}
	return ISSUE_LIST;
};

const getJiraTimeUnit = (defaultUnit: JiraTimeUnit): TimeUnit => {
	switch (defaultUnit.toLowerCase()) {
		case TimeUnits.DAY: {
			return TimeUnits.DAY;
		}
		case TimeUnits.HOUR: {
			return TimeUnits.HOUR;
		}
		case TimeUnits.MINUTE: {
			return TimeUnits.MINUTE;
		}
		case TimeUnits.WEEK: {
			return TimeUnits.WEEK;
		}
		default: {
			return TimeUnits.DAY;
		}
	}
};

const getJiraTimeFormat = (format: JiraTimeFormat): TimeFormat => {
	switch (format.toLowerCase()) {
		case TimeFormats.DAYS: {
			return TimeFormats.DAYS;
		}
		case TimeFormats.HOURS: {
			return TimeFormats.HOURS;
		}
		case TimeFormats.PRETTY: {
			return TimeFormats.PRETTY;
		}
		default: {
			return TimeFormats.DAYS;
		}
	}
};

const cardTypesTransformer = (cardTypes: AggSoftwareCardType[]): CardType[] =>
	cardTypes.reduce(
		(
			baseLevelCardTypes: CardType[],
			{ hierarchyLevelType, id, name, iconUrl, hasRequiredFields }: AggSoftwareCardType,
		) => {
			if (String(hierarchyLevelType) === BASE_HIERARCHY_TYPE) {
				baseLevelCardTypes.push({
					id: Number(id),
					name,
					iconUrl,
					hasRequiredFields,
					typeHierarchyLevel: BASE_HIERARCHY_TYPE,
				});
			}
			return baseLevelCardTypes;
		},
		[],
	);

const BACKLOG_LIST = freeze({ id: BACKLOG, type: BACKLOG, name: 'Backlog', issueListKey: null });

export const configTransformer = ({
	jira: { globalTimeTrackingSettings },
	boardScope: {
		board: { rankCustomFieldId },
		projectLocation: { name },
		features,
		currentUser: { permissions },
		estimation,
		backlog,
	},
}: aggBacklogDataQuery$data): ConfigPayload => {
	if (!backlog) {
		throw new Error('boardScope.backlog is null');
	}
	const filteredFeatures = features
		.filter(Boolean)
		.map(({ key, status }) => ({ key: String(key), status: String(status) }));
	const filteredPermissions = permissions.map((permission) => String(permission));
	const transformedConfig = {
		rankCustomFieldId: Number(rankCustomFieldId),
		requestColumnMigration: backlog.requestColumnMigration,
		name,
		permissions: ff('issue-unarchival_wdpb9')
			? {
					[CAN_MANAGE_SPRINTS]: filteredPermissions.includes(MANAGE_SPRINT),
					[CAN_CREATE_ISSUES]: filteredPermissions.includes(CREATE_ISSUE),
					[CAN_EDIT_ISSUES]: filteredPermissions.includes(EDIT_ISSUE),
					[CAN_EDIT_BOARD_CONFIG]: filteredPermissions.includes(EDIT_BOARD_CONFIG),
					[CAN_ARCHIVE_ISSUE]: filteredPermissions.includes(ARCHIVE_ISSUE),
				}
			: {
					[CAN_MANAGE_SPRINTS]: filteredPermissions.includes(MANAGE_SPRINT),
					[CAN_CREATE_ISSUES]: filteredPermissions.includes(CREATE_ISSUE),
					[CAN_EDIT_ISSUES]: filteredPermissions.includes(EDIT_ISSUE),
					[CAN_EDIT_BOARD_CONFIG]: filteredPermissions.includes(EDIT_BOARD_CONFIG),
				},
		version: {
			isVersionsFeatureEnabled: isFeatureEnabled(filteredFeatures, VERSION_FEATURE),
		},
		allFeatures: filteredFeatures,
		backlogStrategy: getBacklogStrategy(filteredFeatures),
		storyPointFieldId: storyPointFieldIdTransformer(estimation),
		timeTrackingOptions: {
			workingDaysPerWeek: globalTimeTrackingSettings.workingDaysPerWeek,
			workingHoursPerDay: globalTimeTrackingSettings.workingHoursPerDay,
			defaultUnit: getJiraTimeUnit(globalTimeTrackingSettings.defaultUnit),
			timeFormat: getJiraTimeFormat(globalTimeTrackingSettings.defaultFormat),
		},
	};
	return transformedConfig;
};

export const cardTransformer = (
	card: NonNullable<AggSoftwareCard>,
	projectAri: AggProjectLocation['id'],
): Card => ({
	summary: card.summary,
	id: Number(getIdFromAri(card.id)),
	key: card.key,
	labels: card.labels.filter(Boolean),
	assignee: card.assignee
		? {
				id: card.assignee.accountId,
				displayName: card.assignee.name,
				avatarUrl: card.assignee.picture,
			}
		: null,
	icon: card.type.iconUrl,
	isDone: card.done,
	isFlagged: card.flagged,
	typeId: Number(card.type.id),
	issueUrl: `/browse/${card.key}`,
	statusId: card.status.id,
	parentId: card.parentId ? Number(getIdFromAri(card.parentId)) : null,
	priority: card.priority
		? {
				name: card.priority.name,
				iconUrl: card.priority.iconUrl,
			}
		: null,
	// @ts-expect-error - Type 'string | null | undefined' is not assignable to type 'string | null'.
	dueDate: card.dueDate,
	// @ts-expect-error - Type '{ timeRemainingEstimate: null; originalEstimate: { readonly value: number; readonly valueAsText: string; } | null | undefined; storyPoints: number | null | undefined; } | null' is not assignable to type 'CardEstimate | null'.
	estimate: isNil(card.estimate) ? null : { ...card.estimate, timeRemainingEstimate: null },
	numCompleteChildren: card.childCardsMetadata ? card.childCardsMetadata.complete : 0,
	numTotalChildren: card.childCardsMetadata ? card.childCardsMetadata.total : 0,
	fixVersions: card.fixVersionsIds.concat(),
	projectId: Number(getIdFromAri(projectAri)),
});

export const cardParentsTransformer = (
	cardParents: AggSoftwareCardParent[],
): IssueParentsEntityState =>
	cardParents.reduce(
		(issueParentsEntityState: IssueParentsEntityState, cardParent: AggSoftwareCardParent) => {
			const cardId = getIdFromAri(cardParent.id);
			const { key, summary, cardType } = cardParent;
			return Object.assign(issueParentsEntityState, {
				[cardId]: {
					id: cardId,
					key,
					summary,
					color: cardParent.color ? String(cardParent.color) : null,
					issueType: {
						id: cardType.id,
						name: cardType.name,
						iconUrl: cardType.iconUrl,
					},
				},
			});
		},
		{},
	);

export const estimationTypeTransformer = (
	isEstimationEnabled: boolean,
	estimation: AggEstimation,
): EstimationType => {
	const estimateFieldKey = estimation.current.customFieldId || '';

	if (isEstimationEnabled && estimateFieldKey) {
		return estimateFieldKey === TimeEstimateKey ? ORIGINAL_ESTIMATE : STORY_POINTS;
	}

	return ISSUE_COUNT;
};

export const estimationFieldIdTransformer = (
	isEstimationEnabled: boolean,
	estimation: AggEstimation,
): string | null =>
	// @ts-expect-error - Type 'string | null | undefined' is not assignable to type 'string | null'.
	isEstimationEnabled ? estimation.current.customFieldId : null;

export const estimationTransformer = (
	features: FeaturesResponse[],
	estimation: AggEstimation,
): EstimationEntityState => {
	const isEstimationFeatureEnabled = isFeatureEnabled(features, ESTIMATION_FEATURE);
	return {
		isEstimationFeatureEnabled,
		estimationType: estimationTypeTransformer(isEstimationFeatureEnabled, estimation),
		estimationFieldId: estimationFieldIdTransformer(isEstimationFeatureEnabled, estimation),
		name: '',
	};
};

export const sprintTransformer = (
	{ id, name, goal, startDate, endDate, daysRemaining, sprintState }: AggSprint,
	canUpdateSprint: boolean,
): CardList => ({
	id: getIdFromAri(id),
	name,
	type: SPRINT,
	issueListKey: null,
	sprintData: {
		goal: goal || '',
		startDate: startDate || '',
		endDate: endDate || '',
		daysRemaining: daysRemaining || 0,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		state: (String(sprintState) as SprintState) || SPRINT_STATE_FUTURE,
		canUpdateSprint,
	},
});

export const versionTransformer = (allCards: CardEntityState): VersionState => ({
	versions: uniq(
		values(allCards).reduce(
			(fixVersions, card) =>
				card.fixVersions && card.fixVersions.length > 0
					? fixVersions.concat(card.fixVersions)
					: fixVersions,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			[] as string[],
		),
	),
});

const projectTransformer = (
	projectLocation: aggBacklogDataQuery$data['boardScope']['projectLocation'],
): ProjectState => ({
	projects: [
		{
			id: Number(getIdFromAri(projectLocation.id)),
			isSimplified: true,
			key: projectLocation.key,
			name: projectLocation.name,
		},
	],
});

const backlogOnlyTransformer = (data: aggBacklogDataQuery$data): SetAppDataPayload => {
	const {
		backlog,
		board,
		cardParents,
		features,
		estimation,
		projectLocation: { id: projectAri, cardTypes },
	} = data.boardScope;

	if (!backlog) {
		throw new Error('boardScope.backlog is null');
	}

	const boardCards = board.cards.filter(Boolean);
	const backlogCards = backlog.cards.filter(Boolean);
	const cards = backlogCards.concat(boardCards);
	const filteredFeatures = features
		.filter(Boolean)
		.map(({ key, status }) => ({ key: String(key), status: String(status) }));
	const filteredCardParents = cardParents.filter(Boolean);
	const card = zipObject(
		cards.map(({ id }) => getIdFromAri(id)),
		cards.map((cardData) => cardTransformer(cardData, projectAri)),
	);

	return {
		entities: {
			cardList: {
				[BOARD]: {
					id: BOARD,
					type: BOARD,
					name: 'Board',
					issueListKey: backlog.boardIssueListKey,
				},
				[BACKLOG]: BACKLOG_LIST,
			},
			cardListOrder: [BOARD, BACKLOG],
			card,
			cardTypes: cardTypesTransformer(cardTypes.filter(Boolean)),
			cardOrder: {
				[BOARD]: boardCards.map(({ id }) => Number(getIdFromAri(id))),
				[BACKLOG]: backlogCards.map(({ id }) => Number(getIdFromAri(id))),
			},
			issueParents: cardParentsTransformer(filteredCardParents),
			issueParentsOrder: filteredCardParents.map(({ id }) => getIdFromAri(id)),
			estimation: estimationTransformer(filteredFeatures, estimation),
			version: versionTransformer(card),
			inlineCreate: {
				cardTypeIds: cardTypes.filter(Boolean).map((it) => Number(it.id)),
			},
			project: fg('jsw-perf-backlog-versions-filter-load')
				? projectTransformer(data.boardScope.projectLocation)
				: undefined,
		},
		config: configTransformer(data),
	};
};

const backlogWithSprintsTransformer = (data: aggBacklogDataQuery$data): SetAppDataPayload => {
	const {
		currentUser: { permissions },
		sprints,
		backlog,
		cardParents,
		features,
		estimation,
		projectLocation: { id: projectId, cardTypes },
	} = data.boardScope;

	if (!backlog) {
		throw new Error('boardScope.backlog is null');
	}

	const filteredBacklogCards = backlog.cards.filter(Boolean);
	const filteredCardParents = cardParents.filter(Boolean);
	const filteredFeatures = features
		.filter(Boolean)
		.map(({ key, status }) => ({ key: String(key), status: String(status) }));

	const filteredSprints = sprints.filter(Boolean);
	const canUpdateSprint = permissions.map(String).includes(MANAGE_SPRINT);
	const cardList: Record<string, CardList> = { [BACKLOG]: BACKLOG_LIST };
	const cardListOrder: Array<string> = [];

	const cardOrder = {
		[BACKLOG]: filteredBacklogCards.map(({ id }) => Number(getIdFromAri(id))),
	};

	let cards: AggSoftwareCard[] = [];

	filteredSprints.forEach((sprint) => {
		const sprintId = getIdFromAri(sprint.id);
		const sprintCards = sprint.cards.filter(Boolean);
		cardList[sprintId] = sprintTransformer(sprint, canUpdateSprint);
		cardListOrder.push(sprintId);
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ BACKLOG: any[]; }'.
		cardOrder[sprintId] = sprintCards.map(({ id }) => Number(getIdFromAri(id)));
		cards = cards.concat(sprintCards);
	});

	cardListOrder.push(BACKLOG);
	cards = cards.concat(filteredBacklogCards);
	const card = zipObject(
		cards.map(({ id }) => getIdFromAri(id)),
		cards.map((cardData) => cardTransformer(cardData, projectId)),
	);
	return {
		entities: {
			cardList,
			cardListOrder,
			card,
			cardTypes: cardTypesTransformer(cardTypes.filter(Boolean)),
			cardOrder,
			issueParents: cardParentsTransformer(cardParents.filter(Boolean)),
			issueParentsOrder: filteredCardParents.map(({ id }) => getIdFromAri(id)),
			estimation: estimationTransformer(filteredFeatures, estimation),
			version: versionTransformer(card),
			inlineCreate: {
				cardTypeIds: cardTypes.filter(Boolean).map((it) => Number(it.id)),
			},
			project: fg('jsw-perf-backlog-versions-filter-load')
				? projectTransformer(data.boardScope.projectLocation)
				: undefined,
		},
		config: configTransformer(data),
	};
};

export const transformAggData = (data: aggBacklogDataQuery$data): SetAppDataPayload => {
	const filteredFeatures = data.boardScope.features
		.filter(Boolean)
		.map(({ key, status }) => ({ key: String(key), status: String(status) }));

	if (isFeatureEnabled(filteredFeatures, SPRINTS_FEATURE)) {
		return backlogWithSprintsTransformer(data);
	}
	return backlogOnlyTransformer(data);
};
